import React, { useMemo, useState } from 'react';
import {
	billingInformationFields,
	contractDetailFields,
} from '../../../components/DataGrid/columnDefinitions';
import { useSelector } from 'react-redux';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useLazyGetAssetsByContractQuery } from '../../../api/asset.api';
import type { RootState } from '../../../redux/store';
import Assets from '../Assets/Assets';
import { selectAccount } from '../../../redux/slices/accountSlice';
import { selectAssetGridRefreshTrigger } from '../../../redux/slices/udfUpdateSlice';
import type { GridDataSource } from '@mui/x-data-grid-pro';
import type { Asset } from '../../../api/models/asset.model';
import type { PaginatedResults } from '../../../api/models/paginatedResults.model';
import type { Contract } from '../../../api/models/contract.model';
import { PropToString } from '../../../utils/helpers';

interface AssetsProps {
	contract: any;
}

const ContractDetails: React.FC<AssetsProps> = ({ contract }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const account = useSelector(selectAccount);
	const contractProps = PropToString<Contract>();

	// Get active filters from Redux
	const activeFilters = useSelector((state: RootState) => state.filters.activeFilters);
	const assetGridRefreshTrigger = useSelector(selectAssetGridRefreshTrigger);

	const [currentAssetsData, setCurrentAssetsData] = useState<Asset[]>([]);

	const [getAssets, { isLoading }] = useLazyGetAssetsByContractQuery();

	const assetDataSource: GridDataSource = useMemo(
		() => ({
			getRows: async ({ paginationModel, sortModel }) => {
				const assetQueryParams = {
					contractNumber: contract.contractNumber,
					accountId: account.accountId,
					sourceId: account.sourceId,
					accountTypeCode: account.accountTypeCode,
					filters: activeFilters,
					pageSize: paginationModel?.pageSize ?? 10,
					currentPage: paginationModel?.page ?? 0,
					sortDirection: sortModel?.[0]?.sort || '',
					sortField: sortModel?.[0]?.field || '',
				};

				const result = (await getAssets(assetQueryParams).unwrap()) as PaginatedResults<Asset>;
				setCurrentAssetsData(result?.content || []);
				return {
					rows: result?.content ?? [],
					rowCount: result?.totalElements ?? 0,
				};
			},
		}),
		[account, contract, activeFilters, assetGridRefreshTrigger]
	);


	// Helper to render field values with special handling for valueFormatter
	const renderFieldValue = (field: any) => {
		const value = contract[field.field];
		if (field.field === String(contractProps.totalAmountFinanced) && (value === null || value === undefined)) {
			return null;
		}
		if (field.valueFormatter && value !== null && value !== undefined) {
			return field.valueFormatter(value);
		}
		return value ?? '-';
	};

	const mobileView = () => (
		<Box
			display="flex"
			flexDirection="column"
			gap={1}
			mt={1}
			data-testid="contract-details-mobile-view"
		>
			<h3>Contract Details</h3>
			<Box>
				{contractDetailFields.map((field, index) => {
					// Hide totalAmountFinanced if value is null/undefined
					if (field.field === String(contractProps.totalAmountFinanced) && (contract[field.field] === null || contract[field.field] === undefined)) {
						return null;
					}
					return (
						<Box key={index} display="flex" flexDirection="column">
							<strong style={{ fontSize: '1.1rem' }} data-testid={`contract-detail-${field.field}`}>
								{field.headerName}:
							</strong>
							<Box mt={0.5} fontSize="1rem" data-testid={`${field.field}-value`}>
								{renderFieldValue(field)}
							</Box>
						</Box>
					);
				})}
			</Box>
			<h3>Billing Information</h3>
			<Box>
				{billingInformationFields.map((field, index) => (
					<Box key={index} display="flex" flexDirection="column">
						<strong style={{ fontSize: '1.1rem' }} data-testid={`contract-detail-${field.field}`}>
							{field.headerName}:
						</strong>
						<Box mt={0.5} fontSize="1rem" data-testid={`${field.field}-value`}>
							{renderFieldValue(field)}
						</Box>
					</Box>
				))}
			</Box>
		</Box>
	);

	const desktopView = () => (
		<Box>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
				<Typography
					variant="h6"
					component="h4"
					sx={{ color: (theme) => theme.palette.primary.main, fontWeight: 'bold' }}
				>
					Contract Details
				</Typography>
			</Box>
			<Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} mt={2}>
				{contractDetailFields.map((field, index) => {
					if (field.field === 'totalAmountFinanced' && (contract[field.field] === null || contract[field.field] === undefined)) {
						return null;
					}
					return (
						<Box key={index} display="flex" flexDirection="column">
							<strong style={{ fontSize: '1.1rem' }} data-testid={`contract-detail-${field.field}`}>
								{field.headerName}:
							</strong>
							<Box mt={0.5} fontSize="1rem" data-testid={`${field.field}-value`}>
								{renderFieldValue(field)}
							</Box>
						</Box>
					);
				})}
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4 }}>
				<Typography
					variant="h6"
					component="h4"
					sx={{ color: (theme) => theme.palette.primary.main, fontWeight: 'bold' }}
				>
					Billing Information
				</Typography>
			</Box>
			<Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} mt={2}>
				{billingInformationFields.map((field, index) => (
					<Box key={index} display="flex" flexDirection="column">
						<strong style={{ fontSize: '1.1rem' }} data-testid={`contract-detail-${field.field}`}>
							{field.headerName}:
						</strong>
						<Box mt={0.5} fontSize="1rem" data-testid={`${field.field}-value`}>
							{renderFieldValue(field)}
						</Box>
					</Box>
				))}
			</Box>
		</Box>
	);

	return (
		<>
			<Box
				sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}
				data-testid="contract-details-header"
			>
				<Typography variant="h3" component="h3" sx={{ fontWeight: 'bold' }}>
					Contract No. {contract.contractNumber}
				</Typography>
			</Box>
			{isMobile ? mobileView() : desktopView()}
			<Assets
				assetsDataSource={assetDataSource}
				assetData={currentAssetsData}
				dataIsLoading={isLoading}
				refreshTrigger={assetGridRefreshTrigger}
			/>
		</>
	);
};

export default ContractDetails;
